import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../api';

const ROUTE_OPTIONS = [
  { value: 'supervisor', label: 'My Supervisor (auto-route to reporter\'s team supervisor)' },
];

const LINKED_MODULE_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'truck_maintenance', label: 'Truck Maintenance (auto-create work order)' },
  { value: 'incidents', label: 'Incidents (auto-create incident report)' },
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

const FIELD_TYPES = ['text', 'textarea', 'select', 'number'];

export default function IssueTemplateSetupPage() {
  const [templates, setTemplates] = useState([]);
  const [positions, setPositions] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = list view, object = form

  const loadData = useCallback(async () => {
    try {
      const [t, p, tm] = await Promise.all([
        api.getIssueTemplates(),
        api.getPositions(),
        api.getTeams(),
      ]);
      setTemplates(t);
      setPositions(p);
      setTeams(tm);
    } catch (err) {
      console.error('Failed to load:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  function startNew() {
    setEditing({
      name: '', description: '', category: '',
      available_to_positions: [], routes_to: 'supervisor',
      linked_module: '', fields: [], priority_default: 'normal', is_active: 1,
    });
  }

  function startEdit(template) {
    setEditing({
      id: template.id,
      name: template.name,
      description: template.description || '',
      category: template.category || '',
      available_to_positions: template.available_to_positions || [],
      routes_to: template.routes_to || 'supervisor',
      linked_module: template.linked_module || '',
      fields: template.fields || [],
      priority_default: template.priority_default || 'normal',
      is_active: template.is_active,
    });
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading...</div>;

  if (editing) {
    return (
      <TemplateForm
        template={editing}
        positions={positions}
        teams={teams}
        onSave={async (data) => {
          if (data.id) {
            await api.updateIssueTemplate(data.id, data);
          } else {
            await api.createIssueTemplate(data);
          }
          setEditing(null);
          loadData();
        }}
        onCancel={() => setEditing(null)}
      />
    );
  }

  // Group by category
  const byCategory = {};
  for (const t of templates) {
    const cat = t.category || 'Uncategorized';
    if (!byCategory[cat]) byCategory[cat] = [];
    byCategory[cat].push(t);
  }

  return (
    <div style={{ padding: '24px 32px', maxWidth: 900 }}>
      <div className="page-header">
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#111827', margin: 0 }}>Issue Templates</h1>
          <p style={{ color: '#6b7280', fontSize: 14, margin: '4px 0 0' }}>
            Configure issue types employees can report — routing, fields, and linked modules
          </p>
        </div>
        <button className="btn btn-primary" onClick={startNew}
          style={{ backgroundColor: '#D4A843', color: '#1a1a2e', fontWeight: 600 }}>
          + New Template
        </button>
      </div>

      {templates.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>📋</div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>No issue templates yet</div>
          <div style={{ fontSize: 13 }}>Create templates so employees can report issues</div>
        </div>
      ) : (
        Object.entries(byCategory).map(([cat, catTemplates]) => (
          <div key={cat} style={{ marginBottom: 20 }}>
            <h3 style={{ fontSize: 13, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
              {cat}
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {catTemplates.map(t => (
                <div key={t.id} onClick={() => startEdit(t)} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '14px 20px', backgroundColor: t.is_active ? '#fff' : '#f9fafb',
                  borderRadius: 10, border: '1px solid #e5e7eb', cursor: 'pointer',
                  opacity: t.is_active ? 1 : 0.6,
                }} className="hover-lift">
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: '#111827' }}>{t.name}</div>
                    {t.description && <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>{t.description}</div>}
                    <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, backgroundColor: '#eff6ff', color: '#3b82f6' }}>
                        Routes: {t.routes_to}
                      </span>
                      {t.linked_module && (
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, backgroundColor: '#fef3c7', color: '#92400e' }}>
                          Links: {t.linked_module}
                        </span>
                      )}
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, backgroundColor: '#f3f4f6', color: '#6b7280' }}>
                        {(t.fields || []).length} field{(t.fields || []).length !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                  <span style={{ color: '#d1d5db', fontSize: 18 }}>›</span>
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function TemplateForm({ template, positions, teams, onSave, onCancel }) {
  const [form, setForm] = useState({ ...template });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  function set(key, val) { setForm(f => ({ ...f, [key]: val })); }

  // Build route options dynamically from positions and teams
  const routeOptions = [
    { value: 'supervisor', label: 'Reporter\'s Supervisor (auto-route)' },
    ...teams.map(t => ({ value: `team:${t.id}`, label: `Team: ${t.name} supervisors` })),
    ...positions.filter(p => p.is_supervisor).map(p => ({ value: `position:${p.id}`, label: `Position: ${p.name} (${p.team_name})` })),
  ];

  // Position groups for multi-select
  const positionsByTeam = {};
  for (const p of positions) {
    if (!positionsByTeam[p.team_name]) positionsByTeam[p.team_name] = [];
    positionsByTeam[p.team_name].push(p);
  }

  function togglePosition(posId) {
    const current = form.available_to_positions || [];
    if (current.includes(posId)) {
      set('available_to_positions', current.filter(id => id !== posId));
    } else {
      set('available_to_positions', [...current, posId]);
    }
  }

  function addField() {
    set('fields', [...(form.fields || []), { key: `field_${Date.now()}`, label: '', type: 'text', required: false }]);
  }

  function updateField(index, updates) {
    const fields = [...form.fields];
    fields[index] = { ...fields[index], ...updates };
    set('fields', fields);
  }

  function removeField(index) {
    set('fields', form.fields.filter((_, i) => i !== index));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    setError('');
    try {
      await onSave(form);
    } catch (err) {
      setError(err.message || 'Failed to save');
    }
    setSaving(false);
  }

  return (
    <div style={{ padding: '24px 32px', maxWidth: 700 }}>
      <button onClick={onCancel} style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 13, fontWeight: 600, marginBottom: 12, padding: 0 }}>
        ← Back to templates
      </button>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: '0 0 20px' }}>
        {form.id ? 'Edit Template' : 'New Issue Template'}
      </h1>

      {error && <div style={{ padding: '8px 12px', backgroundColor: '#fef2f2', color: '#ef4444', borderRadius: 6, fontSize: 13, marginBottom: 12 }}>{error}</div>}

      <form onSubmit={handleSubmit}>
        {/* Basic Info */}
        <div style={{ padding: 16, backgroundColor: '#f9fafb', borderRadius: 10, border: '1px solid #f3f4f6', marginBottom: 16 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#374151', margin: '0 0 12px' }}>Basic Info</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label className="sales-label">Template Name *</label>
              <input className="sales-input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Truck Issue" style={{ width: '100%' }} required />
            </div>
            <div>
              <label className="sales-label">Category</label>
              <input className="sales-input" value={form.category} onChange={e => set('category', e.target.value)} placeholder="e.g. Vehicle, Safety, Equipment" style={{ width: '100%' }} />
            </div>
          </div>
          <div style={{ marginTop: 12 }}>
            <label className="sales-label">Description</label>
            <input className="sales-input" value={form.description} onChange={e => set('description', e.target.value)} placeholder="Brief explanation for employees" style={{ width: '100%' }} />
          </div>
        </div>

        {/* Routing */}
        <div style={{ padding: 16, backgroundColor: '#f9fafb', borderRadius: 10, border: '1px solid #f3f4f6', marginBottom: 16 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#374151', margin: '0 0 12px' }}>Routing & Priority</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label className="sales-label">Routes To *</label>
              <select className="sales-input" value={form.routes_to} onChange={e => set('routes_to', e.target.value)} style={{ width: '100%' }}>
                {routeOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="sales-label">Default Priority</label>
              <select className="sales-input" value={form.priority_default} onChange={e => set('priority_default', e.target.value)} style={{ width: '100%' }}>
                {PRIORITY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
            <div>
              <label className="sales-label">Linked Module</label>
              <select className="sales-input" value={form.linked_module} onChange={e => set('linked_module', e.target.value)} style={{ width: '100%' }}>
                {LINKED_MODULE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            {form.id !== undefined && (
              <div>
                <label className="sales-label">Status</label>
                <select className="sales-input" value={form.is_active} onChange={e => set('is_active', Number(e.target.value))} style={{ width: '100%' }}>
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </div>
            )}
          </div>
        </div>

        {/* Available To Positions */}
        <div style={{ padding: 16, backgroundColor: '#f9fafb', borderRadius: 10, border: '1px solid #f3f4f6', marginBottom: 16 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#374151', margin: '0 0 4px' }}>Available To</h3>
          <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 12px' }}>
            Which positions can see this template? Leave empty for all positions.
          </p>
          {Object.entries(positionsByTeam).map(([teamName, teamPos]) => (
            <div key={teamName} style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 4 }}>{teamName}</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {teamPos.map(p => {
                  const selected = (form.available_to_positions || []).includes(p.id);
                  return (
                    <button key={p.id} type="button" onClick={() => togglePosition(p.id)} style={{
                      padding: '4px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                      border: selected ? '1px solid #3b82f6' : '1px solid #e5e7eb',
                      backgroundColor: selected ? '#eff6ff' : '#fff',
                      color: selected ? '#3b82f6' : '#6b7280',
                    }}>
                      {p.name}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Custom Fields */}
        <div style={{ padding: 16, backgroundColor: '#f9fafb', borderRadius: 10, border: '1px solid #f3f4f6', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, color: '#374151', margin: 0 }}>Custom Fields</h3>
            <button type="button" onClick={addField} style={{
              padding: '4px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600,
              border: '1px solid #e5e7eb', backgroundColor: '#fff', color: '#374151', cursor: 'pointer',
            }}>+ Add Field</button>
          </div>
          {(form.fields || []).length === 0 ? (
            <p style={{ fontSize: 12, color: '#9ca3af', textAlign: 'center', padding: 12 }}>No custom fields — only title and description will be shown</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {form.fields.map((field, i) => (
                <div key={i} style={{
                  display: 'grid', gridTemplateColumns: '2fr 1fr auto auto', gap: 8, alignItems: 'center',
                  padding: '8px 12px', backgroundColor: '#fff', borderRadius: 8, border: '1px solid #e5e7eb',
                }}>
                  <input className="sales-input" value={field.label} onChange={e => updateField(i, { label: e.target.value, key: field.key || `field_${i}` })} placeholder="Field label" style={{ width: '100%' }} />
                  <select className="sales-input" value={field.type} onChange={e => updateField(i, { type: e.target.value })} style={{ width: '100%' }}>
                    {FIELD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>
                    <input type="checkbox" checked={field.required || false} onChange={e => updateField(i, { required: e.target.checked })} /> Req
                  </label>
                  <button type="button" onClick={() => removeField(i)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" className="btn" onClick={onCancel} style={{ color: '#6b7280' }}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving || !form.name.trim()}
            style={{ backgroundColor: '#D4A843', color: '#1a1a2e', fontWeight: 600 }}>
            {saving ? 'Saving...' : form.id ? 'Update Template' : 'Create Template'}
          </button>
        </div>
      </form>
    </div>
  );
}
